import { type LucideIcon } from 'lucide-react';

import { cn } from '../lib/cn';

type TabDef<V extends string> = {
    value: V;
    label: string;
    icon?: LucideIcon;
    disabled?: boolean;
    badge?: string | number;
};

type TabsProps<V extends string> = {
    value: V;
    onValueChange: (value: V) => void;
    tabs: TabDef<V>[];
    /**
     * `id` of the panel(s) controlled by this tab list. Pairs with the
     * `aria-controls` on each tab and the matching `id` on the panel
     * container so screen readers can navigate between them.
     */
    panelId?: string;
    /**
     * Disable the built-in bottom border. Useful when the tab list is
     * mounted inside another container (e.g. Dialog's `headerSlot`)
     * that already draws its own divider.
     */
    bordered?: boolean;
    /** `sm` tightens the per-tab padding for narrow containers (e.g. a side
     *  rail). Defaults to `md`. */
    size?: 'sm' | 'md';
    className?: string;
};

/**
 * Minimal accessible tab list — buttons + active-state styling. Keeps
 * the v2 dependency footprint small (no @radix-ui/react-tabs needed
 * for a use case this lightweight). Pair with `<TabPanel>` (just a
 * `<div role="tabpanel">`) or render the active panel inline; this
 * primitive only owns the tab list itself.
 */
export function Tabs<V extends string>({
    value,
    onValueChange,
    tabs,
    panelId,
    bordered = true,
    size = 'md',
    className,
}: TabsProps<V>) {
    return (
        <div
            role="tablist"
            aria-orientation="horizontal"
            className={cn(
                'flex',
                bordered && 'border-b border-solid border-gray-200 dark:border-gray-700',
                className,
            )}
        >
            {tabs.map((tab) => {
                const Icon = tab.icon;
                const selected = tab.value === value;
                return (
                    <button
                        key={tab.value}
                        type="button"
                        role="tab"
                        aria-selected={selected}
                        aria-controls={panelId}
                        tabIndex={selected ? 0 : -1}
                        disabled={tab.disabled}
                        onClick={() => !tab.disabled && onValueChange(tab.value)}
                        className={cn(
                            // The -mb-px lets the active tab's bottom
                            // border overlap the container's divider,
                            // so the active tab reads as "joined" to
                            // the content area below — the standard
                            // browser-tab look.
                            'relative -mb-px inline-flex items-center gap-2 border-b-2 border-solid text-sm font-medium transition-colors',
                            size === 'sm' ? 'px-3 py-2' : 'px-4 py-3',
                            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-0',
                            selected
                                ? 'border-blue-600 text-blue-700 dark:border-blue-400 dark:text-blue-300'
                                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-900 dark:text-gray-400 dark:hover:border-gray-600 dark:hover:text-gray-100',
                            tab.disabled &&
                                'cursor-not-allowed opacity-50 hover:border-transparent hover:text-gray-500 dark:hover:text-gray-400',
                        )}
                    >
                        {Icon && <Icon aria-hidden="true" className="h-4 w-4 shrink-0" />}
                        <span>{tab.label}</span>
                        {tab.badge != null && (
                            <span
                                className={cn(
                                    'ml-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-semibold',
                                    selected
                                        ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-200'
                                        : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
                                )}
                            >
                                {tab.badge}
                            </span>
                        )}
                    </button>
                );
            })}
        </div>
    );
}
